import { apiConfig } from '@/config/authConfig';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: string;
}

class AdminApiService {
  private baseUrl: string;
  private getToken: (() => Promise<string>) | null = null;

  constructor() {
    this.baseUrl = apiConfig.baseUrl;
  }

  setTokenProvider(tokenProvider: () => Promise<string>) {
    this.getToken = tokenProvider;
  }

  private async fetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    if (!this.getToken) {
      throw new Error('Token provider not set');
    }

    const token = await this.getToken();

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API error: ${response.status}`);
    }

    const data: ApiResponse<T> = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Unknown error');
    }

    return data.data as T;
  }

  // Dashboard
  async getDashboard() {
    return this.fetch<{
      totalSubscriptions: number;
      activeSubscriptions: number;
      totalAdultLearners: number;
      totalSchools: number;
      totalLeads: number;
      recentLeads: number;
      recentActivity: Array<{
        id: string;
        type: string;
        description: string;
        timestamp: string;
      }>;
    }>('/dashboard');
  }

  // Subscriptions
  async getSubscriptions(params?: { status?: string; accountType?: string; page?: number; limit?: number }) {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.accountType) query.set('accountType', params.accountType);
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());

    return this.fetch<{
      subscriptions: Array<{
        parentId: string;
        firstName: string;
        lastName: string;
        email: string;
        accountType: string;
        subscriptionStatus: string;
        maxChildren: number;
        childCount: number;
        createdAt: string;
        subscriptionStartDate: string | null;
        subscriptionEndDate: string | null;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(`/subscriptions?${query}`);
  }

  async updateSubscription(parentId: string, data: { subscriptionStatus?: string; maxChildren?: number }) {
    return this.fetch<{ success: boolean }>(`/subscriptions/${parentId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Students
  async getStudents(params?: { parentId?: string; role?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) {
    const query = new URLSearchParams();
    if (params?.parentId) query.set('parentId', params.parentId);
    if (params?.role) query.set('role', params.role);
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortOrder) query.set('sortOrder', params.sortOrder);

    return this.fetch<{
      students: Array<{
        studentId: string;
        firstName: string;
        lastName: string;
        email: string;
        role: string;
        gradeLevel: number | null;
        personalityType: string | null;
        personalityCompletedAt: string | null;
        lastActivityAt: string | null;
        createdAt: string;
        parent: {
          parentId: string;
          firstName: string;
          lastName: string;
          email: string;
        } | null;
        progress: {
          completedLessons: number;
          totalLessons: number;
          percentComplete: number;
          totalTimeMinutes: number;
          completedTests: number;
          totalTests: number;
          averageTestScore: number | null;
        };
      }>;
      total: number;
      page: number;
      limit: number;
    }>(`/students?${query}`);
  }

  async getStudentDetails(studentId: string) {
    return this.fetch<{
      studentId: string;
      firstName: string;
      lastName: string;
      email: string;
      gradeLevel: number | null;
      personalityType: string | null;
      personalityCompletedAt: string | null;
      lastActivityAt: string | null;
      createdAt: string;
      parent: {
        parentId: string;
        firstName: string;
        lastName: string;
        email: string;
      } | null;
      lessonProgress: Array<{
        lessonId: number;
        status: string;
        score: number | null;
        timeSpentMinutes: number;
        completedAt: string | null;
      }>;
    }>(`/students/${studentId}`);
  }

  // Schools
  async getSchools(params?: { page?: number; limit?: number }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());

    return this.fetch<{
      schools: Array<{
        schoolId: string;
        name: string;
        schoolCode: string;
        district: string;
        state: string;
        subscriptionTier: string;
        isActive: boolean;
        createdAt: string;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(`/schools?${query}`);
  }

  async updateSchool(schoolId: string, data: Partial<{
    name: string;
    district: string;
    state: string;
    subscriptionTier: string;
  }>) {
    return this.fetch<{ success: boolean }>(`/schools/${schoolId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Co-ops

  // Leads
  async getLeads(params?: {
    leadSource?: string;
    leadType?: string;
    isSubscribed?: boolean;
    page?: number;
    limit?: number
  }) {
    const query = new URLSearchParams();
    if (params?.leadSource) query.set('leadSource', params.leadSource);
    if (params?.leadType) query.set('leadType', params.leadType);
    if (params?.isSubscribed !== undefined) query.set('isSubscribed', params.isSubscribed.toString());
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());

    return this.fetch<{
      leads: Array<{
        leadId: string;
        email: string;
        firstName: string | null;
        lastName: string | null;
        leadSource: string;
        leadType: string;
        isSubscribed: boolean;
        emailSendCount: number;
        createdAt: string;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(`/leads?${query}`);
  }

  async createLead(data: {
    email: string;
    firstName?: string;
    lastName?: string;
    leadSource: string;
    leadType: string;
    notes?: string;
  }) {
    return this.fetch<{ leadId: string }>('/leads', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLead(leadId: string, data: Partial<{
    firstName: string;
    lastName: string;
    leadType: string;
    isSubscribed: boolean;
    notes: string;
  }>) {
    return this.fetch<{ success: boolean }>(`/leads/${leadId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteLead(leadId: string) {
    return this.fetch<{ success: boolean }>(`/leads/${leadId}`, {
      method: 'DELETE',
    });
  }

  // Campaigns
  async getCampaigns(params?: { status?: string; page?: number; limit?: number }) {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());

    return this.fetch<{
      campaigns: Array<{
        campaignId: string;
        name: string;
        subject: string;
        status: string;
        targetLeadTypes: string | null;
        targetLeadSources: string | null;
        totalRecipients: number;
        totalSent: number;
        sentAt: string | null;
        createdAt: string;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(`/campaigns?${query}`);
  }

  async getCampaign(campaignId: string) {
    return this.fetch<{
      campaignId: string;
      name: string;
      subject: string;
      htmlContent: string;
      plainTextContent: string;
      status: string;
      targetLeadTypes: string | null;
      targetLeadSources: string | null;
      totalRecipients: number;
      totalSent: number;
      sentAt: string | null;
      createdAt: string;
      createdBy: string;
    }>(`/campaigns/${campaignId}`);
  }

  async createCampaign(data: {
    name: string;
    subject: string;
    htmlContent: string;
    plainTextContent: string;
    targetLeadTypes?: string;
    targetLeadSources?: string;
  }) {
    return this.fetch<{ campaignId: string }>('/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCampaign(campaignId: string, data: Partial<{
    name: string;
    subject: string;
    htmlContent: string;
    plainTextContent: string;
    targetLeadTypes: string;
    targetLeadSources: string;
    status: string;
  }>) {
    return this.fetch<{ success: boolean }>(`/campaigns/${campaignId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getCampaignRecipients(campaignId: string) {
    return this.fetch<{
      recipients: Array<{
        leadId: string;
        email: string;
        firstName: string | null;
        lastName: string | null;
      }>;
      total: number;
    }>(`/campaigns/${campaignId}/recipients`);
  }

  async sendCampaign(campaignId: string, leadIds?: string[]) {
    return this.fetch<{
      success: boolean;
      totalSent: number;
      errors: string[];
    }>(`/campaigns/${campaignId}/send`, {
      method: 'POST',
      body: leadIds ? JSON.stringify({ leadIds }) : undefined,
    });
  }

  async deleteCampaign(campaignId: string) {
    return this.fetch<{ success: boolean }>(`/campaigns/${campaignId}`, {
      method: 'DELETE',
    });
  }

  async previewCampaign(campaignId: string, testEmail: string) {
    return this.fetch<{ success: boolean }>(`/campaigns/${campaignId}/preview`, {
      method: 'POST',
      body: JSON.stringify({ testEmail }),
    });
  }

  // Surveys
  async getSurveys(params?: {
    respondentType?: string;
    willingToReviewFacebook?: string;
    willingToShare?: string;
    page?: number;
    limit?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.respondentType) query.set('respondentType', params.respondentType);
    if (params?.willingToReviewFacebook) query.set('willingToReviewFacebook', params.willingToReviewFacebook);
    if (params?.willingToShare) query.set('willingToShare', params.willingToShare);
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());

    return this.fetch<{
      surveys: Array<{
        surveyId: string;
        respondentType: 'student' | 'parent';
        respondentName: string;
        respondentEmail: string;
        studentName: string;
        ratings: Record<string, number>;
        freeText: string | null;
        willingToReviewFacebook: boolean | null;
        willingToShare: boolean | null;
        completedAt: string | null;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(`/surveys?${query}`);
  }

  async getSurveyStats() {
    return this.fetch<{
      totalCompleted: number;
      studentCount: number;
      parentCount: number;
      studentAverages: Record<string, number>;
      parentAverages: Record<string, number>;
      facebookWillingCount: number;
      shareWillingCount: number;
    }>('/surveys/stats');
  }

  // Content Batches
  async getContentBatches(params?: { status?: string; page?: number; limit?: number }) {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());

    return this.fetch<{
      batches: Array<{
        batchId: string;
        weekStartDate: string;
        planSummary: string | null;
        status: string;
        totalPosts: number;
        imagesRendered: number;
        notificationSentAt: string | null;
        createdAt: string;
        finalizedAt: string | null;
        platforms: Record<string, number>;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(`/content-batches?${query}`);
  }

  async getContentBatch(batchId: string) {
    return this.fetch<{
      batchId: string;
      weekStartDate: string;
      planSummary: string | null;
      status: string;
      totalPosts: number;
      imagesRendered: number;
      notificationSentAt: string | null;
      createdAt: string;
      finalizedAt: string | null;
      posts: Array<{
        postId: string;
        platform: string;
        postText: string;
        category: string;
        hookType: string | null;
        sortOrder: number;
        parentPostId: string | null;
        scheduledFor: string | null;
        imageTemplate: string | null;
        imageHeadline: string | null;
        imageBody: string | null;
        imageAccent: string | null;
        imagePath: string | null;
        imageRenderedAt: string | null;
        status: string;
        createdAt: string;
      }>;
    }>(`/content-batches/${batchId}`);
  }

  async updateContentBatch(batchId: string, data: { status: string }) {
    return this.fetch<{ success: boolean }>(`/content-batches/${batchId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async updateContentPost(postId: string, data: Partial<{ postText: string; scheduledFor: string; status: string }>) {
    return this.fetch<{ success: boolean }>(`/content-posts/${postId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }
}

export const adminApi = new AdminApiService();
